/**
 * Catalog data layer - fetches categories & products from the backend API,
 * normalizes them to the shape the storefront components expect, and falls
 * back to the bundled static catalog if the API is empty or unreachable.
 */
import { apiUrl } from '../api';
import {
  STATIC_CATEGORIES,
  STATIC_TOP_PRODUCTS,
  STATIC_BESTSELLERS,
  getStaticProductById,
  getStaticProductsByCategoryName,
  checkStaticDelivery,
  STATIC_IMAGES,
} from './staticData';

const FALLBACK_IMG = STATIC_IMAGES.product;

// Normalize an API product into the rich shape components rely on.
export function normalizeProduct(p) {
  if (!p) return null;
  const variants = Array.isArray(p.variants) && p.variants.length
    ? p.variants
    : [{ weight: p.Weight || 'One size', price: p.Price || 0 }];
  const images = Array.isArray(p.Images) && p.Images.length ? p.Images : [FALLBACK_IMG];
  return {
    _id: p._id,
    productName: p.productName,
    Tagline: p.Tagline || '',
    ProductDesc: p.ProductDesc || '',
    Price: variants[0].price,
    Weight: variants[0].weight,
    Images: images,
    Ingredients: Array.isArray(p.Ingredients) && p.Ingredients.length ? p.Ingredients : ['Premium ingredients'],
    Benefits: Array.isArray(p.Benefits) ? p.Benefits : [],
    isSoldOut: !!p.isSoldOut,
    isSignature: !!p.isSignature,
    isTopProduct: !!p.isTopProduct,
    Category: p.Category && p.Category._id
      ? { _id: p.Category._id, Name: p.Category.Name }
      : (typeof p.Category === 'string' ? { _id: p.Category, Name: '' } : p.Category),
    variants,
  };
}

// The API sleeps when idle (Render free tier) and can take ~30-60s to wake.
// Without a deadline, fetch() just hangs and every caller below sits on its
// loading screen instead of falling back to the bundled catalog.
const API_TIMEOUT_MS = 12000;

async function getJson(path) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
  try {
    const res = await fetch(apiUrl(path), { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/** Categories - falls back to static. */
export async function fetchCategories() {
  try {
    const data = await getJson('/api/categories');
    if (data.success && data.data && data.data.length) {
      return data.data.map((c) => ({ _id: c._id, Name: c.Name, Subtitle: c.Subtitle, CollectionTitle: c.CollectionTitle, Description: c.Description }));
    }
  } catch { /* fall through */ }
  return STATIC_CATEGORIES;
}

/** All products (normalized) - falls back to static flat list. */
export async function fetchProducts() {
  try {
    const data = await getJson('/api/products');
    if (data.success && data.data && data.data.length) {
      return data.data.map(normalizeProduct);
    }
  } catch { /* fall through */ }
  // build a flat list from the static category groups
  const flat = [];
  STATIC_TOP_PRODUCTS.forEach((t) => [t.Product1, t.Product2, t.Product3].forEach((p) => p && flat.push(p)));
  return flat;
}

/** Top products grouped by category (3 per category) for grids. */
export async function fetchTopProducts() {
  try {
    const [cats, products] = await Promise.all([fetchCategories(), fetchProducts()]);
    if (products && products.length && cats && cats.length) {
      const groups = cats.map((cat) => {
        const inCat = products.filter((p) => p.Category && p.Category._id === cat._id);
        // Admin-flagged top products first, then fill from the rest (always up to 3)
        const flagged = inCat.filter((p) => p.isTopProduct);
        const rest = inCat.filter((p) => !p.isTopProduct);
        const top = [...flagged, ...rest].slice(0, 3);
        return {
          category: { _id: cat._id, Name: cat.Name },
          Product1: top[0] || null,
          Product2: top[1] || null,
          Product3: top[2] || null,
        };
      }).filter((g) => g.Product1);
      if (groups.length) return groups;
    }
  } catch { /* fall through */ }
  return STATIC_TOP_PRODUCTS;
}

/** Signature Favourites - exactly 3: admin-flagged first, then filled. */
export async function fetchBestsellers() {
  try {
    const products = await fetchProducts();
    if (products && products.length) {
      const flagged = products.filter((p) => p.isSignature);
      const rest = products.filter((p) => !p.isSignature);
      return [...flagged, ...rest].slice(0, 3);
    }
  } catch { /* fall through */ }
  return STATIC_BESTSELLERS.slice(0, 3);
}

/** Single product by id. */
export async function fetchProductById(id) {
  try {
    const data = await getJson(`/api/products/${id}`);
    if (data.success && data.data) return normalizeProduct(data.data);
  } catch { /* fall through */ }
  return getStaticProductById(id);
}

/** Products of a category by name. */
export async function fetchProductsByCategoryName(name) {
  try {
    const data = await getJson(`/api/products/category/name/${encodeURIComponent(name)}`);
    if (data.success && Array.isArray(data.data)) return data.data.map(normalizeProduct);
  } catch { /* fall through */ }
  return getStaticProductsByCategoryName(name);
}

/** Products of a category by id (uses the cached/all list). */
export async function fetchProductsByCategoryId(categoryId) {
  const products = await fetchProducts();
  return products.filter((p) => p.Category && p.Category._id === categoryId);
}

/** Delivery check - uses the API when the pincode exists there, else demo pincodes. */
export async function checkDelivery(pinCode) {
  try {
    const data = await getJson(`/api/delivery/check/${encodeURIComponent(String(pinCode).trim())}`);
    if (data && data.available) return data;
  } catch { /* fall through */ }
  return checkStaticDelivery(pinCode);
}

/** Site settings (hero images, founder image, etc.). Returns { heroImage, heroImageMobile, founderImage }. */
export async function fetchSettings() {
  try {
    const data = await getJson('/api/settings');
    if (data.success && data.settings) return data.settings;
  } catch { /* fall through */ }
  return {
    heroImage: '',
    heroImageMobile: '',
    founderImage: '',
    fulfilment: {
      localDeliveryEnabled: true,
      storePickupEnabled: true,
      storePickup: { address: '', hours: '', instructions: '' },
      timeSlots: [
        { value: '12-2', label: '12:00 PM - 2:00 PM', startHour: 12, enabled: true },
        { value: '2-4', label: '2:00 PM - 4:00 PM', startHour: 14, enabled: true },
        { value: '4-6', label: '4:00 PM - 6:00 PM', startHour: 16, enabled: true },
        { value: '6-8', label: '6:00 PM - 8:00 PM', startHour: 18, enabled: true },
      ],
    },
  };
}

export { STATIC_IMAGES };
